import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { getInstallation, deleteInstallation, postInstallation, updateInstallationVersion } from '../../actions';
import integrationRoutes from '../../helpers/routes';
import { FetchError, IInstallation } from '../../models';
import { installationStates } from '../../static/constants';
import { RootState } from '../rootReducer';
import { getBootstrapAction } from './BootstrapSlice';

export interface IInstallationState {
    installation: IInstallation;
    isSyncing: boolean;
    isFetching: boolean;
    isDeleting: boolean;
    error?: Error;
}

export const initialState: IInstallationState = {
    installation: {
        connectionType: 'unset',
        displayName: 'unset',
        installationId: '',
        integrationType: 'CUSTOM',
        requiresHostname: false,
        signUpUrl: 'unset',
        state: installationStates.CONNECTION_REQUIRED,
    },
    isSyncing: false,
    isFetching: false,
    isDeleting: false,
};

const name = 'installation';

const installationSlice = createSlice({
    initialState,
    name,
    reducers: {
        updateInstallation(state, action: PayloadAction<Partial<IInstallation>>) {
            state.installation = {
                ...state.installation,
                ...action.payload,
            };
            state.isSyncing = false;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(getBootstrapAction.fulfilled, (state, { payload }) => {
                state.installation = {
                    ...state.installation,
                    ...payload.installation,
                };
                state.installation.requiresHostname =
                    !!payload.installation?.authorizeUrl && payload.installation?.authorizeUrl.includes('%s');
                state.error = undefined;
            })
            .addCase(getBootstrapAction.rejected, (state, { payload }) => {
                state.error = payload;
            })
            .addCase(getInstallationAction.pending, (state) => {
                state.isFetching = true;
            })
            .addCase(getInstallationAction.fulfilled, (state, { payload }) => {
                state.isFetching = false;
                state.installation = payload;
                state.error = undefined;
            })
            .addCase(getInstallationAction.rejected, (state, { payload }) => {
                state.isFetching = false;
                state.error = payload;
            })
            .addCase(postInstallationAction.pending, (state) => {
                state.isSyncing = true;
            })
            .addCase(postInstallationAction.fulfilled, (state, {}) => {
                state.isSyncing = false;
                state.error = undefined;
            })
            .addCase(postInstallationAction.rejected, (state, { payload }) => {
                state.isSyncing = false;
                state.error = payload;
            })
            .addCase(updateInstallationVersionAction.pending, (state) => {
                state.isSyncing = true;
            })
            .addCase(updateInstallationVersionAction.fulfilled, (state, {}) => {
                state.isSyncing = false;
                state.error = undefined;
            })
            .addCase(updateInstallationVersionAction.rejected, (state, { payload }) => {
                state.isSyncing = false;
                state.error = payload;
            })
            .addCase(deleteInstallationAction.pending, (state) => {
                state.isDeleting = true;
            })
            .addCase(deleteInstallationAction.fulfilled, (state, {}) => {
                state.isDeleting = false;
                state.error = undefined;
            })
            .addCase(deleteInstallationAction.rejected, (state, { payload }) => {
                state.isDeleting = false;
                state.error = payload;
            });
    },
});

export const getInstallationAction = createAsyncThunk<
    IInstallation,
    void,
    { state: RootState; rejectValue: FetchError }
>(`${name}/getInstallation`, async (_, { getState, rejectWithValue }) => {
    try {
        const state = getState();
        return await getInstallation(state.user.user, state.installation.installation);
    } catch (error) {
        if (error instanceof FetchError) {
            // api response errors i.e. 400 and 404
            return rejectWithValue({ name: error.name, message: error.message, code: error.code, body: error.body });
        }
        return rejectWithValue(error);
    }
});

export const postInstallationAction = createAsyncThunk<
    IInstallation,
    void,
    { state: RootState; rejectValue: FetchError }
>(`${name}/postInstallation`, async (_, { getState, rejectWithValue }) => {
    try {
        const state = getState();
        return await postInstallation(state.user.user, state.installation.installation);
    } catch (error) {
        if (error instanceof FetchError) {
            // api response errors i.e. 400 and 404
            return rejectWithValue({ name: error.name, message: error.message, code: error.code, body: error.body });
        }
        return rejectWithValue(error);
    }
});

export const deleteInstallationAction = createAsyncThunk<void, boolean, { state: RootState; rejectValue: FetchError }>(
    `${name}/deleteInstallation`,
    async (cancelSubscription: boolean, { getState, rejectWithValue }) => {
        try {
            const state = getState();
            return await deleteInstallation(state.user.user, state.installation.installation, cancelSubscription);
        } catch (error) {
            if (error instanceof FetchError) {
                // api response errors i.e. 400 and 404
                return rejectWithValue({
                    name: error.name,
                    message: error.message,
                    code: error.code,
                    body: error.body,
                });
            }
            return rejectWithValue(error);
        }
    }
);

export const updateInstallationVersionAction = createAsyncThunk<
    void,
    void,
    { state: RootState; rejectValue: FetchError }
>(`${name}/updateInstallationVersion`, async (_, { getState, rejectWithValue }) => {
    try {
        const state = getState();
        return await updateInstallationVersion(state.user.user, state.installation.installation);
    } catch (error) {
        if (error instanceof FetchError) {
            // api response errors i.e. 400 and 404
            return rejectWithValue({ name: error.name, message: error.message, code: error.code, body: error.body });
        }
        return rejectWithValue(error);
    }
});

export const { updateInstallation } = installationSlice.actions;

export const installationSelectors = {
    installation: (state: RootState) => state.installation.installation,
    isFetching: (state: RootState) => state.installation.isFetching,
    isSyncing: (state: RootState) => state.installation.isSyncing,
    isDeleting: (state: RootState) => state.installation.isDeleting,
    routes: (state: RootState) => integrationRoutes.get(state.installation.installation.integrationType),
    error: (state: RootState) => state.installation.error,
};

export default installationSlice.reducer;
